import {
  Box,
  Button,
  LinearProgress,
  Link,
  Typography,
  Alert,
  AlertTitle,
  Paper,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useSubmitScreening } from "../../hooks/tenants/useSubmitScreening";
import AdvancedInput from "../elements/AdvancedInput";
import { useState } from "react";
import theme from "../../styles/styleTheme";
import { useTranslation } from "react-i18next";
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

type FinalReviewContainerProps = {
  candidate: any;
  triggers: any[];
  allLinkedProfilesComplete: boolean;
  generalInfoComplete: boolean;
  hasRenterProfile: boolean;
};

const FinalReviewContainer = ({
  candidate,
  triggers,
  allLinkedProfilesComplete,
  generalInfoComplete,
  hasRenterProfile,
}: FinalReviewContainerProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useForm({});
  const { submit } = useSubmitScreening();
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const scrollTop = () => {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(triggers.map((trigger) => trigger()));
      const allValid = results.every((result) => result === true);

      if (allValid && allLinkedProfilesComplete) {
        await submit(candidate.id);
        window.location.reload();
      }
      scrollTop();
    } catch (error) {
      console.error("Error during submission:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
        <Box sx={{ marginTop: "1.5rem", display: "flex", justifyContent: "flex-start" }}>
          <AdvancedInput
            control={control}
            name="terms"
            errors={errors}
            changeValue={(e: any) => {
              setTermsAgreed(e.target.checked);
            }}
            type={"checkbox"}
            customErrorMessage={t('screening.review.terms_error')}
            label={
              <Typography variant="body2" sx={{ ml: 1 }}>
                {t('screening.review.terms_text', {
                  terms: (
                    <Link
                      href="https://gohome.rent/algemene-voorwaarden/"
                      target="_blank"
                    >
                      {t('screening.review.terms_link')}
                    </Link>
                  )
                })}
              </Typography>
            }
          />
        </Box>
        
        {!hasRenterProfile && (
          <Paper 
            elevation={0}
            sx={{ 
              mt: 3,
              mb: 2,
              p: 2,
              backgroundColor: 'rgba(211, 47, 47, 0.05)',
              border: `1px solid rgba(211, 47, 47, 0.3)`,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <WarningAmberIcon sx={{ color: 'rgb(211, 47, 47)' }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'rgb(178, 34, 34)' }}>
                {t('screening.review.renter_required_title')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgb(128, 0, 0)' }}>
                {t('screening.review.renter_required')}
              </Typography>
            </Box>
          </Paper>
        )}
        
        <Button
          variant={"contained"}
          disabled={loading || !termsAgreed || !allLinkedProfilesComplete || !generalInfoComplete || !hasRenterProfile}
          onClick={onSubmit}
          sx={{ 
            mt: 2,
            padding: ".5rem 3rem",
            backgroundColor: theme.palette.secondary.main,
            color: "#fff", 
          }}
        >
          {t('screening.review.submit')}
        </Button>
        {loading && <LinearProgress color="primary" />}
    </>
  );
};

export default FinalReviewContainer;
